import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { QrCode, Loader2, Scan, AlertCircle } from 'lucide-react';
import api from '../../lib/api';

const EventScannerLogin = () => {
  const { eventSlug } = useParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const [eventError, setEventError] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const { loginScannerByEvent } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!eventSlug) {
        setEventError('No event provided');
        setLoadingEvent(false);
        return;
      }
      
      try {
        const response = await api.get(`/scanner/${eventSlug}/info`);
        setEventInfo(response.data);
        setEventError(null);
      } catch (error) {
        console.error('Failed to fetch event info:', error);
        setEventError(error.response?.data?.detail || 'Event not found or unavailable');
      } finally {
        setLoadingEvent(false);
      }
    };
    
    fetchEventInfo();
  }, [eventSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await loginScannerByEvent(eventSlug, username, password);
      toast.success('Login successful');
      navigate('/scanner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (loadingEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-700 bg-slate-800/50 backdrop-blur">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white">Event Not Found</CardTitle>
              <CardDescription className="text-slate-400 mt-2">
                {eventError}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-slate-500 mb-4">
              Please check the scanner URL or contact your event administrator.
            </p>
            <a 
              href="/scanner/login" 
              className="text-emerald-400 hover:underline text-sm"
            >
              Use Campaign ID login instead
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandingColor = eventInfo?.branding_color || '#059669';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center space-y-4">
          <div 
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ backgroundColor: brandingColor }}
          >
            <Scan className="h-8 w-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-white">Scanner Login</CardTitle>
            <CardDescription className="text-slate-400">
              {eventInfo?.event_name || 'Event Scanner'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">Username</Label>
              <Input
                id="username"
                placeholder="Your scanner username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="scanner-username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="scanner-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full text-white font-medium"
              style={{ 
                backgroundColor: brandingColor,
                '--tw-ring-color': brandingColor 
              }}
              disabled={loading}
              data-testid="scanner-login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <QrCode className="mr-2 h-4 w-4" />
                  Access Scanner
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-slate-700">
            <p className="text-xs text-center text-slate-500">
              Scanner for: <span className="text-slate-300">{eventInfo?.event_name}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventScannerLogin;
